// The MapLibre canvas: one Map instance, GeoJSON sources refreshed from query
// data, hover tooltips, and click-through to entity pages.
import { useEffect, useRef, useState } from "react";
import maplibregl, { Map as MlMap, type MapGeoJSONFeature } from "maplibre-gl";
import { useLocation } from "wouter";
import { featureCollection, toFeature } from "../lib/geo";
import type { EventPin, OutingPin, PlacePin, RegionShape, RoutePin } from "../lib/queries";
import { useResolvedTheme } from "../lib/theme";
import { basemapStyle } from "./basemap";
import {
  CLUSTER_LAYERS,
  CLUSTER_SOURCE_OPTIONS,
  DRAW_SOURCES,
  DRAW_VERTEX_LAYER,
  drawLayerSpecs,
  INTERACTIVE_LAYERS,
  layerSpecs,
  SOURCES,
} from "./layers";
import { MapTooltip, type TooltipState } from "./MapTooltip";

export type Camera = { lng: number; lat: number; zoom: number };

export type MapPressPoint = { lng: number; lat: number; x: number; y: number };

/** Draw-a-route mode state; `selected` is the index of the selected vertex. */
export type DrawState = { points: [number, number][]; selected: number | null };

type Props = {
  initialCamera: Camera;
  onViewportChange: (bounds: { minLon: number; minLat: number; maxLon: number; maxLat: number }, zoom: number) => void;
  onCameraChange: (camera: Camera) => void;
  /**
   * Right-click on desktop, long-press where the browser maps it to a DOM
   * `contextmenu` event (Android does; iOS Safari doesn't fire contextmenu on
   * the canvas, so iOS users add places via right-click-equivalent gestures
   * or not at all — accepted for the prototype). MapLibre relays the DOM
   * event verbatim; it does not synthesize one from a long touch.
   */
  onContextMenu?: (point: MapPressPoint) => void;
  places: PlacePin[];
  routes: RoutePin[];
  regions: RegionShape[];
  outings: OutingPin[];
  events: EventPin[];
  /** Cluster the point-pin sources at low zoom. Default on. */
  clustering?: boolean;
  flyTo?: { lng: number; lat: number; zoom: number } | null;
  /**
   * Non-null puts the map in draw-a-route mode: entity clicks/tooltips are
   * suppressed, a click appends a vertex (or selects one), and vertices drag.
   */
  draw?: DrawState | null;
  onDrawChange?: (next: DrawState) => void;
};

export function MapView({ initialCamera, onViewportChange, onCameraChange, onContextMenu, places, routes, regions, outings, events, clustering = true, flyTo, draw, onDrawChange }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MlMap | null>(null);
  const [ready, setReady] = useState(false);
  // Bumped on every style.load (initial load AND basemap swaps) so the data
  // effect re-pushes source data after setStyle wipes the sources.
  const [styleEpoch, setStyleEpoch] = useState(0);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [, navigate] = useLocation();
  const viewportTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const theme = useResolvedTheme();
  const appliedTheme = useRef(theme);

  // Callbacks live in refs so map event handlers never go stale. `draw` rides
  // along because the once-registered handlers branch on the current mode, and
  // `clustering` so ensureDataLayers registers sources with the current flag.
  const callbacks = useRef({ onViewportChange, onCameraChange, onContextMenu, navigate, draw, onDrawChange, clustering });
  callbacks.current = { onViewportChange, onCameraChange, onContextMenu, navigate, draw, onDrawChange, clustering };
  // Set by the mount effect; lets the clustering effect below tear down and
  // re-register sources, which only the mount closure can do.
  const rebuildDataLayers = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: basemapStyle(appliedTheme.current),
      center: [initialCamera.lng, initialCamera.lat],
      zoom: initialCamera.zoom,
      attributionControl: { compact: true },
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "bottom-right");
    map.addControl(new maplibregl.GeolocateControl({}), "bottom-right");

    const emitViewport = () => {
      clearTimeout(viewportTimer.current);
      viewportTimer.current = setTimeout(() => {
        const b = map.getBounds();
        const center = map.getCenter();
        callbacks.current.onViewportChange(
          { minLon: b.getWest(), minLat: b.getSouth(), maxLon: b.getEast(), maxLat: b.getNorth() },
          map.getZoom(),
        );
        callbacks.current.onCameraChange({ lng: center.lng, lat: center.lat, zoom: map.getZoom() });
      }, 300);
    };

    // Fires on the initial load and again after every setStyle: the new style
    // arrives without our sources/layers, so re-register them each time.
    const ensureDataLayers = () => {
      for (const id of Object.values(SOURCES)) {
        // Clustering is a source-creation option, so it must be applied here —
        // it can't be toggled on a live source, and setData works on clustered
        // sources exactly as on plain ones.
        if (!map.getSource(id))
          map.addSource(id, {
            type: "geojson",
            data: featureCollection([]),
            ...(callbacks.current.clustering ? CLUSTER_SOURCE_OPTIONS[id] : undefined),
          });
      }
      for (const id of Object.values(DRAW_SOURCES)) {
        if (!map.getSource(id)) map.addSource(id, { type: "geojson", data: featureCollection([]) });
      }
      for (const layer of [...layerSpecs(appliedTheme.current), ...drawLayerSpecs()]) {
        if (!map.getLayer(layer.id)) map.addLayer(layer);
      }
      setStyleEpoch((e) => e + 1);
    };
    map.on("style.load", ensureDataLayers);

    // Toggling clustering means re-creating the clustered sources. All data
    // layers come off first (a source can't be removed while a layer uses it,
    // and re-adding the full spec list keeps the z-order intact), then
    // ensureDataLayers re-registers everything and bumps styleEpoch so the
    // data effect re-pushes into the fresh sources. The untouched sources
    // (route lines, regions, draw) survive and just get their layers back.
    rebuildDataLayers.current = () => {
      for (const layer of [...layerSpecs(appliedTheme.current), ...drawLayerSpecs()]) {
        if (map.getLayer(layer.id)) map.removeLayer(layer.id);
      }
      for (const id of Object.keys(CLUSTER_SOURCE_OPTIONS)) {
        if (map.getSource(id)) map.removeSource(id);
      }
      ensureDataLayers();
    };

    map.on("load", () => {
      // Layer-scoped listeners are delegated by layer id, so registering them
      // once here survives basemap swaps.
      for (const layerId of INTERACTIVE_LAYERS) {
        map.on("mousemove", layerId, (e) => {
          if (callbacks.current.draw) return;
          const feature = e.features?.[0];
          if (!feature) return;
          map.getCanvas().style.cursor = "pointer";
          setTooltip({ x: e.point.x, y: e.point.y, feature: featureInfo(feature) });
        });
        map.on("mouseleave", layerId, () => {
          if (callbacks.current.draw) return;
          map.getCanvas().style.cursor = "";
          setTooltip(null);
        });
        map.on("click", layerId, (e) => {
          if (callbacks.current.draw) return;
          const feature = e.features?.[0];
          if (!feature) return;
          const { kind, id } = feature.properties as { kind: string; id: string };
          callbacks.current.navigate(`/${kind}/${id}`);
        });
      }

      // Clusters navigate nowhere — a click zooms to the level where the
      // cluster breaks apart. Unclustered pins keep the kind/id navigation
      // above (the pin layers filter out cluster features, so the two sets of
      // handlers never see each other's features).
      for (const { id: layerId, source } of CLUSTER_LAYERS) {
        map.on("mousemove", layerId, () => {
          if (callbacks.current.draw) return;
          map.getCanvas().style.cursor = "pointer";
        });
        map.on("mouseleave", layerId, () => {
          if (callbacks.current.draw) return;
          map.getCanvas().style.cursor = "";
        });
        map.on("click", layerId, async (e) => {
          if (callbacks.current.draw) return;
          const feature = e.features?.[0];
          const clusterId = feature?.properties?.cluster_id as number | undefined;
          if (!feature || clusterId == null) return;
          const src = map.getSource(source) as maplibregl.GeoJSONSource | undefined;
          if (!src) return;
          try {
            const zoom = await src.getClusterExpansionZoom(clusterId);
            const [lng, lat] = (feature.geometry as GeoJSON.Point).coordinates as [number, number];
            map.easeTo({ center: [lng, lat], zoom, duration: 600 });
          } catch {
            // The cluster can vanish mid-flight (setData during the lookup);
            // a dead click is the right outcome.
          }
        });
      }

      // Draw-a-route interactions: click on a vertex → select it; click on
      // empty map → append a vertex; drag a vertex to move it. All no-ops
      // unless the draw prop is set.
      map.on("click", (e) => {
        const d = callbacks.current.draw;
        if (!d) return;
        const hits = map.getLayer(DRAW_VERTEX_LAYER)
          ? map.queryRenderedFeatures(e.point, { layers: [DRAW_VERTEX_LAYER] })
          : [];
        if (hits.length) {
          callbacks.current.onDrawChange?.({ ...d, selected: hits[0]!.properties.index as number });
        } else {
          callbacks.current.onDrawChange?.({ points: [...d.points, [e.lngLat.lng, e.lngLat.lat]], selected: null });
        }
      });

      let dragging: number | null = null;
      map.on("mousedown", DRAW_VERTEX_LAYER, (e) => {
        const d = callbacks.current.draw;
        if (!d) return;
        e.preventDefault();
        dragging = e.features?.[0]?.properties.index as number;
        callbacks.current.onDrawChange?.({ ...d, selected: dragging });
        map.dragPan.disable();
        map.getCanvas().style.cursor = "grabbing";
      });
      map.on("mousemove", (e) => {
        if (dragging == null) return;
        const d = callbacks.current.draw;
        if (!d) return;
        const idx = dragging;
        callbacks.current.onDrawChange?.({
          ...d,
          points: d.points.map((p, i) => (i === idx ? [e.lngLat.lng, e.lngLat.lat] : p)),
        });
      });
      map.on("mouseup", () => {
        if (dragging == null) return;
        dragging = null;
        map.dragPan.enable();
        map.getCanvas().style.cursor = "crosshair";
      });
      map.on("mouseenter", DRAW_VERTEX_LAYER, () => {
        if (callbacks.current.draw && dragging == null) map.getCanvas().style.cursor = "pointer";
      });
      map.on("mouseleave", DRAW_VERTEX_LAYER, () => {
        if (callbacks.current.draw && dragging == null) map.getCanvas().style.cursor = "crosshair";
      });

      setReady(true);
      emitViewport();
    });
    map.on("moveend", emitViewport);
    map.on("contextmenu", (e) => {
      // preventDefault suppresses the browser's own context menu on desktop.
      e.preventDefault();
      callbacks.current.onContextMenu?.({ lng: e.lngLat.lng, lat: e.lngLat.lat, x: e.point.x, y: e.point.y });
    });

    return () => {
      clearTimeout(viewportTimer.current);
      rebuildDataLayers.current = null;
      map.remove();
      mapRef.current = null;
    };
    // The map is created exactly once; camera changes flow through flyTo and
    // basemap swaps through setStyle below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rebuild the data sources when the clustering toggle changes; skipped on
  // mount (appliedClustering starts equal) and while the map is still loading
  // (ensureDataLayers reads the live flag on first style.load anyway).
  const appliedClustering = useRef(clustering);
  useEffect(() => {
    if (!ready || appliedClustering.current === clustering) return;
    appliedClustering.current = clustering;
    setTooltip(null);
    rebuildDataLayers.current?.();
  }, [ready, clustering]);

  // Swap the basemap when the theme changes. setStyle drops our sources and
  // layers; the style.load handler re-adds them and bumps styleEpoch so the
  // data effect below re-pushes the current data.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || appliedTheme.current === theme) return;
    appliedTheme.current = theme;
    setTooltip(null);
    map.setStyle(basemapStyle(theme), { diff: false });
  }, [theme]);

  // Push data into sources whenever query results change or the style reloads.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || styleEpoch === 0) return;
    const set = (sourceId: string, data: GeoJSON.FeatureCollection) =>
      (map.getSource(sourceId) as maplibregl.GeoJSONSource | undefined)?.setData(data);

    set(
      SOURCES.places,
      featureCollection(
        places.map((p) =>
          toFeature(p.geom, { kind: "place", id: p.id, name: p.name, type: p.type, elevation: p.elevation }),
        ),
      ),
    );
    set(
      SOURCES.routes,
      featureCollection(
        routes.map((r) =>
          toFeature(r.startPoint, {
            kind: "route",
            id: r.id,
            name: r.name,
            activities: r.activities.join(", "),
            durationMin: r.durationMin,
          }),
        ),
      ),
    );
    set(
      SOURCES.routeLines,
      featureCollection(
        routes
          .filter((r) => r.mainGeom)
          .map((r) =>
            toFeature(r.mainGeom, { kind: "route", id: r.id, name: r.name, activities: r.activities.join(", ") }),
          ),
      ),
    );
    set(
      SOURCES.outings,
      featureCollection(
        outings.map((o) =>
          toFeature(o.point, {
            kind: "outing",
            id: o.id,
            name: o.title,
            activity: o.activity,
            date: o.date,
            rating: o.rating,
            status: o.status,
          }),
        ),
      ),
    );
    set(
      SOURCES.regions,
      featureCollection(regions.map((rg) => toFeature(rg.geomSimple, { kind: "region", id: rg.id, name: rg.name, type: rg.type }))),
    );
    set(
      SOURCES.events,
      featureCollection(
        events.map((ev) => toFeature(ev.location, { kind: "event", id: ev.id, name: ev.title, startsAt: ev.startsAt })),
      ),
    );
  }, [ready, styleEpoch, places, routes, regions, outings, events]);

  // Push draw geometry; both sources empty out when draw mode ends.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || styleEpoch === 0) return;
    const points = draw?.points ?? [];
    const line: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features:
        points.length >= 2
          ? [{ type: "Feature", geometry: { type: "LineString", coordinates: points }, properties: {} }]
          : [],
    };
    const vertices: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features: points.map((p, i) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: p },
        properties: { index: i, selected: i === draw?.selected },
      })),
    };
    (map.getSource(DRAW_SOURCES.line) as maplibregl.GeoJSONSource | undefined)?.setData(line);
    (map.getSource(DRAW_SOURCES.vertices) as maplibregl.GeoJSONSource | undefined)?.setData(vertices);
  }, [ready, styleEpoch, draw]);

  // Crosshair while drawing; entering draw mode also drops any open tooltip.
  const drawActive = draw != null;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    map.getCanvas().style.cursor = drawActive ? "crosshair" : "";
    if (drawActive) setTooltip(null);
  }, [ready, drawActive]);

  useEffect(() => {
    if (flyTo && mapRef.current) {
      mapRef.current.flyTo({ center: [flyTo.lng, flyTo.lat], zoom: flyTo.zoom, duration: 1200 });
    }
  }, [flyTo]);

  return (
    <div className="relative h-full w-full">
      <div ref={containerRef} className="h-full w-full" />
      <MapTooltip tooltip={tooltip} />
    </div>
  );
}

function featureInfo(feature: MapGeoJSONFeature): Record<string, unknown> {
  return feature.properties ?? {};
}
